import { useState, useCallback } from "react";

export interface SortingOptions {
  initial?: SortState;
  onSortChange?: (sort: SortState) => void;
}

export interface SortingState {
  sort: SortState;
  setSort: (sort: SortState) => void;
  toggle: (columnId: string) => void;
  clear: () => void;
}

export enum SortDirection {
  ASC = "asc",
  DESC = "desc",
}

export interface SortState {
  id: string | null;
  dir: SortDirection;
}

const DEFAULT_SORT: SortState = { id: null, dir: SortDirection.ASC };

export function useSorting(options: SortingOptions = {}): SortingState {
  const { initial = DEFAULT_SORT, onSortChange } = options;

  const [sort, setSortState] = useState<SortState>(initial);

  const setSort = useCallback(
    (newSort: SortState) => {
      setSortState(newSort);
      onSortChange?.(newSort);
    },
    [onSortChange]
  );

  const toggle = useCallback(
    (columnId: string) => {
      setSortState((current) => {
        let newSort: SortState;

        if (current.id !== columnId) {
          newSort = { id: columnId, dir: SortDirection.ASC };
        } else if (current.dir === SortDirection.ASC) {
          newSort = { id: columnId, dir: SortDirection.DESC };
        } else {
          newSort = { id: null, dir: SortDirection.ASC };
        }

        onSortChange?.(newSort);
        return newSort;
      });
    },
    [onSortChange]
  );

  const clear = useCallback(() => {
    const newSort = { id: null, dir: SortDirection.DESC as SortDirection };
    setSortState(newSort);
    onSortChange?.(newSort);
  }, [onSortChange]);

  return {
    sort,
    setSort,
    toggle,
    clear,
  };
}
